import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Music,
  X,
  ChevronDown,
  ChevronUp,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  ExternalLink,
  Plus,
  Trash2,
  Link2,
} from 'lucide-react';
import { cn } from '../lib/utils';

interface SpotifyTrack {
  id: string;
  type: 'track' | 'album' | 'playlist' | 'artist' | 'episode' | 'show';
  name: string;
  uri: string;
}

interface SpotifyPlayerProps {
  className?: string;
}

// Helper to extract Spotify ID and type from URL
function parseSpotifyUrl(url: string): { type: string; id: string } | null {
  const patterns = [
    /spotify\.com\/(track|album|playlist|artist|episode|show)\/([a-zA-Z0-9]+)/,
    /spotify:(\w+):([a-zA-Z0-9]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return { type: match[1], id: match[2] };
    }
  }
  return null;
}

// Get embed URL for Spotify content
function getSpotifyEmbedUrl(type: string, id: string, compact = false): string {
  const height = compact ? 80 : 352;
  return `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`;
}

export default function SpotifyPlayer({ className }: SpotifyPlayerProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('ar') ? 'ar' : 'en';
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [inputUrl, setInputUrl] = useState('');
  const [currentTrack, setCurrentTrack] = useState<SpotifyTrack | null>(null);
  const [recentTracks, setRecentTracks] = useState<SpotifyTrack[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load saved tracks from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('spotify_recent_tracks');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecentTracks(parsed);
        if (parsed.length > 0) {
          setCurrentTrack(parsed[0]);
        }
      } catch (e) {
        console.error('Failed to parse saved tracks');
      }
    }
  }, []);

  // Save tracks to localStorage
  useEffect(() => {
    if (recentTracks.length > 0) {
      localStorage.setItem('spotify_recent_tracks', JSON.stringify(recentTracks.slice(0, 10)));
    }
  }, [recentTracks]);

  const handleAddTrack = () => {
    if (!inputUrl.trim()) return;
    
    const parsed = parseSpotifyUrl(inputUrl.trim());
    if (!parsed) {
      setError(lang === 'ar' ? 'رابط سبوتيفاي غير صالح' : 'Invalid Spotify link');
      return;
    }

    const newTrack: SpotifyTrack = {
      id: parsed.id,
      type: parsed.type as SpotifyTrack['type'],
      name: `${parsed.type.charAt(0).toUpperCase() + parsed.type.slice(1)}`,
      uri: inputUrl.trim(),
    };

    setCurrentTrack(newTrack);
    setRecentTracks(prev => {
      const filtered = prev.filter(t => t.id !== newTrack.id);
      return [newTrack, ...filtered].slice(0, 10);
    });
    setInputUrl('');
    setError(null);
  };

  const handleRemoveTrack = (id: string) => {
    setRecentTracks(prev => prev.filter(t => t.id !== id));
    if (currentTrack?.id === id) {
      const remaining = recentTracks.filter(t => t.id !== id);
      setCurrentTrack(remaining.length > 0 ? remaining[0] : null);
    }
  };

  const handlePlayTrack = (track: SpotifyTrack) => {
    setCurrentTrack(track);
    setIsMinimized(false);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all',
          'hover:bg-secondary/80',
          isOpen && 'bg-secondary text-primary',
          currentTrack && 'text-green-500'
        )}
        title={lang === 'ar' ? 'مشغل سبوتيفاي' : 'Spotify Player'}
      >
        <Music className="h-4 w-4" />
        <span className="hidden md:inline">
          {lang === 'ar' ? 'موسيقى' : 'Music'}
        </span>
        {currentTrack && (
          <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        )}
      </button>

      {/* Player Panel */}
      {isOpen && (
        <div className="absolute bottom-full mb-2 start-0 z-50 w-80 rounded-xl border bg-card shadow-2xl overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-green-500/10 to-transparent">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-green-500" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <span className="font-medium text-sm">
                {lang === 'ar' ? 'سبوتيفاي' : 'Spotify'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 rounded-md hover:bg-secondary transition-colors"
              >
                {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-md hover:bg-secondary transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Add Track Input */}
              <div className="p-3 border-b space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Link2 className="absolute start-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={inputUrl}
                      onChange={(e) => setInputUrl(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTrack()}
                      placeholder={lang === 'ar' ? 'الصق رابط سبوتيفاي...' : 'Paste Spotify link...'}
                      className="input ps-9 h-9 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleAddTrack}
                    className="btn-primary h-9 px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {error && (
                  <p className="text-xs text-destructive">{error}</p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  {lang === 'ar' 
                    ? 'أغنية، ألبوم، بلايليست، بودكاست'
                    : 'Track, album, playlist, or podcast'}
                </p>
              </div>

              {/* Current Player */}
              {currentTrack ? (
                <div className="p-3">
                  <iframe
                    ref={iframeRef}
                    src={getSpotifyEmbedUrl(currentTrack.type, currentTrack.id)}
                    width="100%"
                    height="152"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    className="rounded-lg"
                    style={{ colorScheme: 'normal' }}
                  />
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  <Music className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    {lang === 'ar' 
                      ? 'أضف رابط سبوتيفاي للبدء'
                      : 'Add a Spotify link to get started'}
                  </p>
                </div>
              )}

              {/* Recent Tracks */}
              {recentTracks.length > 0 && (
                <div className="border-t">
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
                    {lang === 'ar' ? 'الأخيرة' : 'Recent'}
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    {recentTracks.map((track) => (
                      <div
                        key={track.id}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 hover:bg-secondary/50 cursor-pointer group',
                          currentTrack?.id === track.id && 'bg-green-500/10'
                        )}
                        onClick={() => handlePlayTrack(track)}
                      >
                        <Music className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        <span className="text-sm truncate flex-1">
                          {track.type}: {track.id.slice(0, 12)}...
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTrack(track.id);
                          }}
                          className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-destructive/20 transition-all"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="px-3 py-2 border-t bg-secondary/30">
                <a
                  href="https://open.spotify.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  {lang === 'ar' ? 'افتح سبوتيفاي' : 'Open Spotify'}
                </a>
              </div>
            </>
          )}

          {/* Minimized View */}
          {isMinimized && currentTrack && (
            <div className="p-3">
              <iframe
                src={getSpotifyEmbedUrl(currentTrack.type, currentTrack.id, true)}
                width="100%"
                height="80"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-lg"
                style={{ colorScheme: 'normal' }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Export a simple embed component for documents
export function SpotifyEmbed({ url, compact = false }: { url: string; compact?: boolean }) {
  const parsed = parseSpotifyUrl(url);
  
  if (!parsed) {
    return (
      <div className="p-4 rounded-lg border border-dashed text-center text-muted-foreground">
        Invalid Spotify link
      </div>
    );
  }

  return (
    <div className="my-4 rounded-xl overflow-hidden shadow-lg">
      <iframe
        src={getSpotifyEmbedUrl(parsed.type, parsed.id, compact)}
        width="100%"
        height={compact ? 80 : 352}
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        style={{ colorScheme: 'normal' }}
      />
    </div>
  );
}

// Export the parse function for use elsewhere
export { parseSpotifyUrl, getSpotifyEmbedUrl };
